import { ChevronDown, ChevronUp, type LucideIcon } from 'lucide-react-native';
import { useState, type ReactNode } from 'react';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type DisclosureProps = {
  label: string;
  testID: string;
  /** Leads the label, so sections that sit together read as a set. */
  icon?: LucideIcon;
  /**
   * Shown in place of the section when it has nothing to open. The row is then
   * plain text rather than a control, so an empty section never invites a tap
   * that opens to nothing, and "none" is said rather than left to be inferred.
   */
  emptyText?: string | null;
  children?: ReactNode;
};

/**
 * A closed section the expert opens when they want it: background to a
 * decision, kept beside the decision without crowding it. Every section on the
 * card uses this one, so they read and behave alike.
 *
 * `aria-expanded` is set beside `accessibilityState`, because
 * react-native-web drops `expanded` from the latter.
 */
export function Disclosure({ label, testID, icon, emptyText, children }: DisclosureProps) {
  const [isOpen, setIsOpen] = useState(false);

  const leadingIcon = icon ? <Icon as={icon} size="sm" className="text-typography-700" /> : null;

  if (emptyText) {
    return (
      <VStack
        className="min-h-12 justify-center gap-0.5 border-t border-outline-200 py-2"
        testID={`${testID}-empty`}
        accessible
        {...getAccessibilityProps({ label: `${label}. ${emptyText}` })}>
        <HStack className="items-center gap-2">
          {leadingIcon}
          <Text size="md" className="flex-1 font-medium text-typography-900">
            {label}
          </Text>
        </HStack>
        <Text size="sm" className={icon ? 'pl-6 text-typography-700' : 'text-typography-700'}>
          {emptyText}
        </Text>
      </VStack>
    );
  }

  return (
    <VStack className="border-t border-outline-200">
      <Pressable
        onPress={() => setIsOpen((open) => !open)}
        className="min-h-12 justify-center web:cursor-pointer"
        testID={`${testID}-toggle`}
        {...getAccessibilityProps({ role: 'button', label })}
        accessibilityState={{ expanded: isOpen }}
        aria-expanded={isOpen}>
        <HStack className="items-center gap-2">
          {leadingIcon}
          <Text size="md" className="flex-1 font-medium text-typography-900">
            {label}
          </Text>
          <Icon as={isOpen ? ChevronUp : ChevronDown} size="md" className="text-typography-700" />
        </HStack>
      </Pressable>

      {isOpen ? (
        <VStack className="pb-4" testID={testID}>
          {children}
        </VStack>
      ) : null}
    </VStack>
  );
}
